import ProposalLink, {
  type ProposalLinkInfo,
} from "@client/components/utils/ProposalLink";
import { Tooltip } from "@client/components/utils/Tooltip";
import { StateContext } from "@client/pages/round/Round";
import { memberIsMeanRecommender } from "@client/utils/memberIsMeanRecommender";
import { RecentIndicator } from "@client/utils/recentProposals";
import { useTheme } from "@client/utils/useTheme";
import { TinyColor } from "@ctrl/tinycolor";
import {
  getEffectiveTrack,
  getRoundMemberForEvaluator,
} from "@engine/round/accessors";
import clsx from "clsx";
import { format } from "d3-format";
import sortBy from "lodash/sortBy";
import { useContext, useState } from "react";
import {
  calculateDisagreementsMatrixData,
  type DisagreementsMatrixData,
  type DisagreementsMatrixRow,
} from "./calculateDisagreementMatrixData";
import { calculateMeanAndStdDev, MatrixTable, MatrixTableEl } from "./common";

type SortOrder = {
  sectionKey: string;
  columnKey: string;
};

export const DisagreementsMatrix = ({
  showMembersOfOtherTracks,
  showRowsWithZeroDisagreements,
}: {
  showMembersOfOtherTracks: boolean;
  showRowsWithZeroDisagreements: boolean;
}) => {
  const { round, actingAsUserId, mode } = useContext(StateContext);
  const viewerTrack = getEffectiveTrack(mode, round.members[actingAsUserId]);

  const [sortOrder, setSortOrder] = useState<SortOrder>({
    sectionKey: "sigma",
    columnKey: "overall",
  });

  const disagreementsMatrixData = calculateDisagreementsMatrixData(round);

  // TODO: ability to sort alphabetically by proposal name
  function getRowSortOrderVaue(row: DisagreementsMatrixRow): number {
    let total = 0;

    if (sortOrder.columnKey === "overall") {
      for (const columnKey of disagreementsMatrixData.keys) {
        total += Math.abs(
          row.sections[sortOrder.sectionKey].values[columnKey] || 0,
        );
      }
    } else {
      total = Math.abs(
        row.sections[sortOrder.sectionKey].values[sortOrder.columnKey] || 0,
      );
    }

    return total;
  }

  const values = getAllDisagreementsValues(disagreementsMatrixData);
  const { mean, standardDeviation } = calculateMeanAndStdDev(values);

  const sortedRows = sortBy(
    disagreementsMatrixData.rows,
    getRowSortOrderVaue,
  ).reverse();

  const filteredKeys =
    showMembersOfOtherTracks || viewerTrack === "NO_TRACK"
      ? disagreementsMatrixData.keys
      : disagreementsMatrixData.keys.filter((key) => {
          if (key === "sigma" || key === "total") {
            return true;
          }
          const member = getRoundMemberForEvaluator(round, key);
          return (
            member &&
            (memberIsMeanRecommender(member) || member.track === viewerTrack)
          );
        });

  return (
    <MatrixTableEl>
      <MatrixTable>
        <Thead>
          <DisagreementsColumnSectionHeadersRow
            data={disagreementsMatrixData}
            setSortOrder={setSortOrder}
            sortOrder={sortOrder}
            filteredKeys={filteredKeys}
          />
          <DisagreementsColumnHeadersRow
            data={disagreementsMatrixData}
            setSortOrder={setSortOrder}
            sortOrder={sortOrder}
            filteredKeys={filteredKeys}
          />
        </Thead>
        <tbody>
          {sortedRows
            .filter((row) => {
              if (showRowsWithZeroDisagreements) {
                return true;
              }
              for (const section of Object.values(row.sections)) {
                for (const value of Object.values(section.values)) {
                  if (value !== 0) {
                    return true;
                  }
                }
              }
              return false;
            })
            .map((row, i) => (
              <DisagreementsProposalRow
                key={row.proposal.id}
                row={row}
                keys={filteredKeys}
                mean={mean}
                standardDeviation={standardDeviation}
                keyNames={disagreementsMatrixData.keyNames}
                firstRow={i === 0}
                isHold={row.proposal.id === round.holdProposalId}
              />
            ))}
        </tbody>
      </MatrixTable>
    </MatrixTableEl>
  );
};

function getAllDisagreementsValues(data: DisagreementsMatrixData): number[] {
  const values: number[] = [];

  for (const sectionKey of data.keys) {
    if (sectionKey === "total") {
      continue;
    }
    for (const columnKey of data.keys) {
      if (sectionKey === columnKey || columnKey === "total") {
        continue;
      }

      for (const row of data.rows) {
        const value = row.sections[sectionKey].values[columnKey];
        if (value == undefined) {
          continue;
        }
        values.push(value);
      }
    }
  }
  return values;
}

type SetDisagreementSortOrder = ({
  sectionKey,
  columnKey,
}: {
  sectionKey: string;
  columnKey: string;
}) => void;

function DisagreementsColumnSectionHeadersRow({
  data,
  setSortOrder,
  sortOrder,
  filteredKeys,
}: {
  data: DisagreementsMatrixData;
  setSortOrder: SetDisagreementSortOrder;
  sortOrder: SortOrder;
  filteredKeys: string[];
}) {
  const sectionColSpan = filteredKeys.length - 1;
  return (
    <tr>
      <th rowSpan={2} />
      {filteredKeys.map((sectionKey, i) => {
        if (sectionKey === "total") {
          return null;
        }
        const sortedOnMe =
          sortOrder.sectionKey === sectionKey &&
          sortOrder.columnKey === "overall";

        return (
          <th
            colSpan={sectionColSpan}
            key={sectionKey}
            className={clsx(
              "p-1 cursor-pointer",
              "border-y border-r border-solid light:border-gray-300 dark:border-[#181818]",
              i === 0 && "border-l",
              "light:bg-white dark:bg-[#3a3a3a]",
            )}
            onClick={() => setSortOrder({ sectionKey, columnKey: "overall" })}
          >
            <Tooltip title={data.keyNames[sectionKey]} placement="bottom-start">
              {data.keyNames[sectionKey] + (sortedOnMe ? " ▼" : " ▶")}
            </Tooltip>
          </th>
        );
      })}
    </tr>
  );
}

function DisagreementsColumnHeadersRow({
  data,
  setSortOrder,
  sortOrder,
  filteredKeys,
}: {
  data: DisagreementsMatrixData;
  setSortOrder: SetDisagreementSortOrder;
  sortOrder: SortOrder;
  filteredKeys: string[];
}) {
  return (
    <tr>
      {filteredKeys.map((sectionKey, i) => {
        if (sectionKey === "total") {
          return null;
        }
        return filteredKeys.map((columnKey, j) => {
          if (sectionKey === columnKey) {
            return null;
          }

          const sortedOnMe =
            sortOrder.sectionKey === sectionKey &&
            sortOrder.columnKey === columnKey;

          return (
            <th
              key={`${sectionKey}-${columnKey}`}
              onClick={() => setSortOrder({ sectionKey, columnKey })}
              className={clsx(
                "cursor-pointer p-1 min-w-[48px]",
                "border-r border-b border-solid light:border-gray-300 dark:border-[#181818]",
                // The first column of the first row should have a left border
                // j === 1 because we skip i === 0, j === 0 by returning null.
                i === 0 && j === 1 && "border-l",
                "light:bg-white dark:bg-[#3a3a3a]",
              )}
            >
              {data.keyNames[columnKey] + (sortedOnMe ? " ▼" : " ▶")}
            </th>
          );
        });
      })}
    </tr>
  );
}

function DisagreementsProposalRow({
  row,
  keys,
  standardDeviation,
  mean,
  keyNames,
  firstRow,
  isHold,
}: {
  row: DisagreementsMatrixRow;
  keys: string[];
  standardDeviation: number;
  mean: number;
  keyNames: { [key: string]: string };
  firstRow: boolean;
  isHold: boolean;
}) {
  const { round } = useContext(StateContext);

  return (
    <tr className="hover:filter light:hover:brightness-90 dark:hover:brightness-75">
      <th
        className={clsx(
          "font-semibold text-left",
          "p-1 min-w-[48px] sticky left-0 z-10",
          "border-b border-x border-solid light:border-gray-300 dark:border-[#181818]",
          firstRow && "border-t",
          "light:bg-white dark:bg-[#3a3a3a]",
        )}
      >
        {isHold ? (
          <span>{row.proposal.shortname}</span>
        ) : (
          <ProposalLink proposal={row.proposal}>
            {row.proposal.shortname}
            <RecentIndicator proposalId={row.proposal.id} />
          </ProposalLink>
        )}
      </th>
      {keys.flatMap((sectionKey) => {
        if (sectionKey === "total") {
          return null;
        }

        return keys.map((columnKey) => {
          if (sectionKey === columnKey) {
            return null;
          }

          //TODO: use a more robust way to determine if a cell should have a link
          const columnName = keyNames[columnKey];
          const noLink =
            row.proposal.shortname === "Hold" ||
            columnName === "Tot" ||
            columnName.toLowerCase().includes("mean") ||
            columnName === "Σ";

          const member = getRoundMemberForEvaluator(round, columnKey);
          let proposalLinkInfo: ProposalLinkInfo | undefined;
          if (!noLink) {
            proposalLinkInfo = { proposal: row.proposal.shortname };
            if (member) {
              proposalLinkInfo.tab = "notes";
              proposalLinkInfo.evaluator = member.initials;
            }
          }

          return (
            <DisagreementMatrixCell
              key={`${sectionKey}-${columnKey}`}
              standardDeviation={standardDeviation}
              mean={mean}
              userInitials={{
                column: keyNames[columnKey],
                section: keyNames[sectionKey],
              }}
              value={row.sections[sectionKey].values[columnKey]}
              proposalLinkInfo={proposalLinkInfo}
            />
          );
        });
      })}
    </tr>
  );
}

function DisagreementMatrixCell({
  value,
  standardDeviation,
  mean,
  userInitials,
  proposalLinkInfo,
  formatValue = true,
}: {
  value: number | undefined | null;
  standardDeviation: number;
  mean: number;
  // A disagreements explanation tooltip will be shown if userInitials is provided
  userInitials: {
    column: string;
    section: string;
  };
  formatValue?: boolean;
  proposalLinkInfo?: ProposalLinkInfo;
}) {
  const { isDarkMode } = useTheme();

  if (value == null) {
    return (
      <td
        className="border-b border-r border-solid z-1 relative
        light:bg-white dark:bg-[#3a3a3a]
        light:border-gray-300 dark:border-[#181818]"
      />
    );
  }

  const zScore = Math.abs(value - mean) / standardDeviation;

  // consider 2 standard deviations to be the maximum of the color scale
  let alpha = Math.min(zScore, 2) / 2;

  const baseColor = value < 0 ? "red" : "green";
  let backgroundColor = new TinyColor(baseColor).setAlpha(alpha);

  if (baseColor === "red") {
    backgroundColor = isDarkMode
      ? backgroundColor.darken(25)
      : backgroundColor.lighten(25);
  }

  if (baseColor === "green") {
    backgroundColor = isDarkMode
      ? backgroundColor
      : backgroundColor.lighten(25);
  }

  if (userInitials.column === "Tot") {
    backgroundColor = new TinyColor(getBackgroundColor(isDarkMode));
  }

  const formattedValue = formatValue
    ? format(".2s")(value)
    : value && value.toFixed(2);
  const cellContentWithLink = proposalLinkInfo ? (
    <ProposalLink {...proposalLinkInfo}>{formattedValue}</ProposalLink>
  ) : (
    formattedValue
  );

  let cellContent = <>{cellContentWithLink}</>;
  const absValue = Math.abs(value);
  const formattedAbsValue = formatValue
    ? format(".2s")(absValue)
    : absValue.toFixed(2);

  let tooltipText = "";
  if (userInitials.column === "Tot") {
    tooltipText = `Total absolute disagreement for section`;
  } else {
    tooltipText = `${userInitials.column} would allocate ${formattedAbsValue} ${value < 0 ? "less" : "more"} compared to ${userInitials.section}`;
  }
  cellContent = <Tooltip title={tooltipText}>{cellContentWithLink}</Tooltip>;

  return (
    <td
      style={{ backgroundColor: backgroundColor.toRgbString() }}
      className="p-1 border-b border-r border-solid light:border-gray-300 dark:border-[#181818] z-1 relative"
    >
      {cellContent}
    </td>
  );
}

export function getBackgroundColor(isDarkMode: boolean | undefined) {
  return isDarkMode ? "#3a3a3a" : "white";
}

export function Thead({
  children,
  highZIndex,
  className = "",
}: {
  children: React.ReactNode;
  highZIndex?: boolean;
  className?: string;
}) {
  return (
    <thead
      className={`sticky top-0 ${highZIndex ? "z-40" : "z-2"} ${className}`}
    >
      {children}
    </thead>
  );
}
